import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Card,
  CardContent,
  Divider,
  useTheme,
  InputAdornment,
  IconButton,
  Switch,
  FormControlLabel,
  alpha,
} from '@mui/material';
import {
  Lock,
  Visibility,
  VisibilityOff,
  Security,
  Email,
  AttachMoney,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { changePassword } from '../../../services/request/Auth';
import { BackdropComponent } from '../../../shared-components/backdrop/Backdrop';
import { usePriceMasking } from '../../../contexts/PriceMaskingContext';
import ApiKeysSettings from './ApiKeysSettings';

const Settings: React.FC = () => {
  const theme = useTheme();
  const { isPriceMasked, togglePriceMasking } = usePriceMasking();
  const [open, setOpen] = useState(false);
  const [showCurrentPin, setShowCurrentPin] = useState(false);
  const [showNewPin, setShowNewPin] = useState(false);
  const [showConfirmPin, setShowConfirmPin] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const formik = useFormik({
    initialValues: {
      email: '',
      currentPin: '',
      newPin: '',
      confirmPin: '',
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email('Invalid email address')
        .required('Email is required'),
      currentPin: Yup.string()
        .required('Current password is required'),
      newPin: Yup.string()
        .required('New password is required')
        .min(8, 'Password must be at least 8 characters long')
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
          'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character (@$!%*?&)'
        )
        .notOneOf([Yup.ref('currentPin')], 'New password must be different from current password'),
      confirmPin: Yup.string()
        .required('Confirm password is required')
        .oneOf([Yup.ref('newPin')], 'Passwords must match'),
    }),
    onSubmit: async (values, { resetForm }) => {
      setError(null);
      setSuccess(null);
      setOpen(true);

      try {
        const response = await changePassword({
          email: values.email,
          currentPin: values.currentPin,
          newPin: values.newPin,
          confirmPin: values.confirmPin,
        });

        setOpen(false);

        if (response.status === 200 && response.data) {
          const apiResponse = response.data;
          if (apiResponse.statusCode === 200) {
            setSuccess('Password changed successfully!');
            toast.success('Password changed successfully!', { position: 'top-right' });
            resetForm();
          } else {
            const errorMsg = apiResponse.message || 'Failed to change password';
            setError(errorMsg);
            toast.error(errorMsg, { position: 'top-right' });
          }
        } else {
          const errorMsg = response.data?.message || 'Failed to change password';
          setError(errorMsg);
          toast.error(errorMsg, { position: 'top-right' });
        }
      } catch (err: any) {
        setOpen(false);
        const errorMsg = err?.response?.data?.message || err?.message || 'Failed to change password';
        setError(errorMsg);
        toast.error(errorMsg, { position: 'top-right' });
      }
    },
  });

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <ToastContainer position="top-right" autoClose={3000} />
      <BackdropComponent open={open} />

      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 4, fontWeight: 700 }}>
        Settings
      </Typography>

      <ApiKeysSettings />

      {/* Price Masking Settings */}
      <Card elevation={3} sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <AttachMoney sx={{ fontSize: 32, color: theme.palette.primary.main }} />
            <Box>
              <Typography variant="h5" component="h2" fontWeight={600}>
                Price Masking
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Hide price amounts across the application when showing to others
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                {isPriceMasked ? 'Prices are currently masked' : 'Prices are currently visible'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isPriceMasked
                  ? 'All price amounts will be displayed as "****" across the application'
                  : 'All price amounts will be displayed normally'}
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={isPriceMasked}
                  onChange={togglePriceMasking}
                  color="primary"
                  size="medium"
                />
              }
              label={isPriceMasked ? 'Masked' : 'Visible'}
              labelPlacement="end"
            />
          </Box>
        </CardContent>
      </Card>

      {/* Password Reset Settings */}
      <Card elevation={3} sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Security sx={{ fontSize: 32, color: theme.palette.primary.main }} />
            <Box>
              <Typography variant="h5" component="h2" fontWeight={600}>
                Password Reset
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Change your admin account password
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Current Password"
                  name="currentPin"
                  type={showCurrentPin ? 'text' : 'password'}
                  value={formik.values.currentPin}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.currentPin && Boolean(formik.errors.currentPin)}
                  helperText={formik.touched.currentPin && formik.errors.currentPin}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle current password visibility"
                          onClick={() => setShowCurrentPin(!showCurrentPin)}
                          edge="end"
                        >
                          {showCurrentPin ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="New Password"
                  name="newPin"
                  type={showNewPin ? 'text' : 'password'}
                  value={formik.values.newPin}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.newPin && Boolean(formik.errors.newPin)}
                  helperText={formik.touched.newPin && formik.errors.newPin}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle new password visibility"
                          onClick={() => setShowNewPin(!showNewPin)}
                          edge="end"
                        >
                          {showNewPin ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Confirm New Password"
                  name="confirmPin"
                  type={showConfirmPin ? 'text' : 'password'}
                  value={formik.values.confirmPin}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.confirmPin && Boolean(formik.errors.confirmPin)}
                  helperText={formik.touched.confirmPin && formik.errors.confirmPin}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle confirm password visibility"
                          onClick={() => setShowConfirmPin(!showConfirmPin)}
                          edge="end"
                        >
                          {showConfirmPin ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={() => formik.resetForm()}
                    disabled={open}
                  >
                    Clear
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={open}
                    startIcon={<Security />}
                    sx={{
                      px: 4,
                      py: 1.5,
                    }}
                  >
                    Change Password
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>

      <Paper sx={{ p: 3, bgcolor: 'background.default' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          <strong>Password Requirements:</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary" component="ul" sx={{ pl: 3, m: 0 }}>
          <li>At least 8 characters long</li>
          <li>At least one uppercase letter (A-Z)</li>
          <li>At least one lowercase letter (a-z)</li>
          <li>At least one number (0-9)</li>
          <li>At least one special character (@$!%*?&)</li>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Make sure to keep your password secure and do not share it with anyone.
        </Typography>
      </Paper>
    </Container>
  );
};

export default Settings;
